/**
 * BrandingContext — Multi-tenant white-label support.
 *
 * On mount, reads window.location.hostname. If it differs from the default
 * CloudSetu domain (connectdesk.cloudsetu.com), fetches partner branding from
 * GET /api/white-label/by-domain and stores it globally.
 *
 * Usage:  const { branding, isCustomDomain } = useBranding();
 */
import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Hostnames that belong to CloudSetu itself — no tenant override
const CLOUDSETU_HOSTS = new Set([
  'connectdesk.cloudsetu.com',
  'cloudsetu.com',
  'www.cloudsetu.com',
  'localhost',
  '127.0.0.1',
]);

const defaultBranding = {
  company_name: 'CloudSetu',
  logo_url: null,
  primary_color: '#0f172a',
  accent_color: '#3b82f6',
  hide_cloudsetu_branding: false,
  subdomain: null,
  custom_domain: null,
  domain_type: 'none',
  reseller_id: null,
};

const BrandingContext = createContext({ branding: defaultBranding, isCustomDomain: false, loading: true });

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(defaultBranding);
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hostname = window.location.hostname;

    // Skip for CloudSetu's own hosts or preview/emergent domains
    const isOwn = CLOUDSETU_HOSTS.has(hostname) ||
      hostname.endsWith('.emergentagent.com') ||
      hostname.endsWith('.preview.emergentagent.com');

    if (isOwn) {
      setLoading(false);
      return;
    }

    // Fetch partner branding for this hostname
    axios.get(`${API}/white-label/by-domain`, { params: { domain: hostname } })
      .then(res => {
        setBranding({ ...defaultBranding, ...res.data, hide_cloudsetu_branding: true });
        setIsCustomDomain(true);
        // Apply brand colors as CSS variables
        document.documentElement.style.setProperty('--brand-primary', res.data.primary_color || '#0f172a');
        document.documentElement.style.setProperty('--brand-accent', res.data.accent_color || '#3b82f6');
        // Update page title
        if (res.data.company_name) document.title = res.data.company_name;
      })
      .catch(() => {
        // Unknown domain — show default CloudSetu branding
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isCustomDomain, loading }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
